import { apiErrorMessage } from '@fieldvision/web-core';
import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { createContext, useContext, useState } from 'react';

import { Button } from './Button.js';
import { Banner } from './primitives.js';

/**
 * Where a popover opened INSIDE this dialog has to render.
 *
 * NOT `document.body`, which is what every portal defaults to and is wrong here
 * twice over. `Dialog` puts its children in an `overflow-y-auto` box, so an
 * in-flow dropdown is clipped by it — but the portal that would escape the
 * clipping lands outside `react-remove-scroll`'s shard (`@radix-ui/react-dialog`
 * mounts `RemoveScroll` with `shards: [contentRef]`), and a list nobody can
 * wheel or drag looks like a list with seven items in it rather than a bug.
 *
 * Portalling into the CONTENT ROOT instead puts the popover outside the scroll
 * box and inside the shard, the focus scope and the `aria-hidden` island, all at
 * once. The root is `flex flex-col` and the scrolling body is its child, so a
 * fixed-position sibling is unclipped.
 *
 * `null` outside a dialog, which is exactly what Radix's `Portal` wants — it
 * falls back to `document.body` for the two filter bars that are not in one.
 */
const DialogPortalContainer = createContext<HTMLElement | null>(null);

export function useDialogPortalContainer(): HTMLElement | null {
  return useContext(DialogPortalContainer);
}

/**
 * The modal, on Radix.
 *
 * Radix rather than hand-rolled because the parts that are easy to get wrong
 * here are the parts nobody tests: focus trapping, restoring focus to the
 * trigger on close, `aria-modal`, Escape, scroll locking, and the outside-click
 * behaviour. Every master-data editor in this app is a form inside one of these,
 * so it is worth the dependency once.
 *
 * `sm:max-w-lg` with a full-height sheet below it — on a phone the dialog is the
 * screen, which is the only honest way to show a twelve-field outlet form at
 * 375px.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * `md` is the form width every editor uses and stays the default. `lg` exists
   * for a READING dialog rather than a form — the price drill-down stacks two
   * trend charts, a per-outlet list and an observation list, and three sections
   * in 32rem is a scroll marathon rather than a comparison.
   */
  size?: 'md' | 'lg';
}) {
  /*
   * `useState` rather than `useRef`: consumers need a re-render when the node
   * arrives, and a ref hands them `null` forever — a popover would silently go
   * back to `document.body` and take the scroll bug with it.
   */
  const [content, setContent] = useState<HTMLDivElement | null>(null);

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-[rgba(18,35,59,0.55)]" />
        <RadixDialog.Content
          ref={setContent}
          // The mobile branch is a full sheet either way; only the desktop
          // max-width varies.
          className={`fixed inset-x-0 bottom-0 top-0 z-50 flex flex-col bg-white shadow-card sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-h-[85vh] sm:w-full sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-3xl ${
            size === 'lg' ? 'sm:max-w-3xl' : 'sm:max-w-lg'
          }`}
        >
          <header className="flex items-start justify-between gap-4 border-b border-line p-5">
            <div>
              <RadixDialog.Title className="font-display text-lg font-bold text-navy">{title}</RadixDialog.Title>
              {description ? (
                <RadixDialog.Description className="mt-1 text-sm text-muted">{description}</RadixDialog.Description>
              ) : null}
            </div>
            <RadixDialog.Close asChild>
              {/*
                `aria-label` carries the name because the button has no text —
                the icon is decorative and must not be the only thing a screen
                reader gets (design-guidelines §8.4).
              */}
              <Button variant="ghost" size="sm" aria-label="Tutup">
                <X size={18} />
              </Button>
            </RadixDialog.Close>
          </header>

          <DialogPortalContainer.Provider value={content}>
            {/*
              THIS BOX IS A SCROLL CONTAINER, so it owes any sticky header inside
              it an offset. Nothing floats above it — the dialog's own header is
              a sibling, outside the scrolling area — so the offset is zero.
              Without this, a header written for the page's 4rem `TopBar` parks
              4rem down from the top of this box and rows scroll past above it.
              See the note in `index.css`.

              THE PADDING IS ON THE INNER DIV, NOT ON THIS ONE, and that is the
              other half of the same bug rather than a formatting preference. A
              scroll container clips its content at the PADDING box, but a sticky
              child offsets from the CONTENT box — so `p-5` here left a 20px band
              at the top of the box that a `top: 0` header could not reach and
              scrolling rows stayed visible in. Padding inside the scrolled
              content has no such edge: the header parks flush against the top of
              the box and the gutter simply scrolls away beneath it.
            */}
            <div
              className="flex-1 overflow-y-auto"
              style={{ '--sticky-header-top': '0px' } as CSSProperties}
            >
              <div className="p-5">{children}</div>
            </div>
          </DialogPortalContainer.Provider>

          {footer ? <footer className="flex justify-end gap-2 border-t border-line p-5">{footer}</footer> : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/**
 * Confirmation for a destructive-ish action.
 *
 * Nothing in this console hard-deletes — master data is deactivated, because
 * visits reference it (ADR-0003). The wording here says "nonaktifkan", not
 * "hapus", so the dialog cannot promise something the API does not do.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel,
  onConfirm,
  pending,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  pending?: boolean;
  /**
   * The failure of the action this dialog confirms, rendered INSIDE it.
   *
   * A confirm dialog stays open when its action fails — the caller closes it in
   * `onSuccess`, which by definition did not run. So a page-level banner ends up
   * behind this overlay, and the user sees a dialog that simply stopped
   * responding to the button. Taking the error as a prop means every confirm in
   * the console reports failure in the place the user is looking, rather than
   * each caller remembering to.
   */
  error?: unknown;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Batal
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={pending}>
            {pending ? 'Memproses…' : confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error ? <Banner tone="error" live>{apiErrorMessage(error)}</Banner> : null}
        <p className="text-sm text-ink">{body}</p>
      </div>
    </Dialog>
  );
}
